/**
 * Changing the signed-in user's password (SPEC decision 38, plan P40): pure rules, no React. The console calls Cognito's
 * `ChangePassword` with the session's access token, so the password never reaches Hangar's API. Only a Cognito user pool
 * (in AWS, or the emulator's locally) supports it; the local dev issuer has no passwords.
 */

/** Cognito's user-pool scope that lets an access token call `ChangePassword` (the app client must allow it). */
export const PASSWORD_SCOPE = 'aws.cognito.signin.user.admin';

const POOL_ID = /^[a-z]{2}(?:-[a-z]+)+-\d_[0-9A-Za-z]+$/;

/**
 * The Cognito API endpoint behind an OIDC authority that is a user pool issuer, else undefined:
 * `https://cognito-idp.<region>.amazonaws.com/<pool id>` → `https://cognito-idp.<region>.amazonaws.com/`, and the
 * emulator's `http://localhost:4567/<pool id>` → `http://localhost:4567/`.
 */
export function cognitoApiOf(authority: string | undefined): string | undefined {
  if (!authority) return undefined;
  let url: URL;
  try {
    url = new URL(authority);
  } catch {
    return undefined;
  }
  const parts = url.pathname.replace(/\/+$/, '').split('/');
  if (!POOL_ID.test(parts.at(-1) ?? '')) return undefined;
  return `${url.origin}${parts.slice(0, -1).join('/')}/`;
}

/** The scopes the console asks for: the password scope only from a user pool (another issuer could refuse it). */
export const scopesFor = (authority: string | undefined): string => (cognitoApiOf(authority) ? `openid email profile ${PASSWORD_SCOPE}` : 'openid email profile');

export interface PasswordChangeInput {
  current: string;
  next: string;
  confirm: string;
}
export type PasswordChangeErrors = Partial<Record<keyof PasswordChangeInput, string>>;

/** The user pool's policy (infra/modules/cognito `password_policy`): 12+ characters, upper and lower case, a number. */
export function passwordPolicyProblem(password: string): string | undefined {
  const missing = [
    password.length < 12 ? 'at least 12 characters' : null,
    /[A-Z]/.test(password) ? null : 'an uppercase letter',
    /[a-z]/.test(password) ? null : 'a lowercase letter',
    /\d/.test(password) ? null : 'a number',
  ].filter(Boolean);
  return missing.length ? `Use ${missing.join(', ')}.` : undefined;
}

/** Field errors before anything is sent; empty when the form may be submitted. */
export function validatePasswordChange(input: PasswordChangeInput): PasswordChangeErrors {
  const errors: PasswordChangeErrors = {};
  if (!input.current) errors.current = 'Enter your current password.';
  const policy = input.next ? passwordPolicyProblem(input.next) : 'Enter a new password.';
  if (policy) errors.next = policy;
  else if (input.next === input.current) errors.next = 'Choose a password different from the current one.';
  if (!input.confirm) errors.confirm = 'Type the new password again.';
  else if (input.confirm !== input.next) errors.confirm = 'The two new passwords do not match.';
  return errors;
}

/** What a refusal from Cognito means for the form: a field to mark, or a message for the whole form. */
export interface PasswordChangeFailure {
  field?: keyof PasswordChangeInput;
  message: string;
}

export function passwordChangeFailure(type: string | undefined, message: string | undefined): PasswordChangeFailure {
  const kind = (type ?? '').split('#').pop();
  if (kind === 'NotAuthorizedException' && /scope/i.test(message ?? '')) {
    return { message: 'This session cannot change the password yet: sign out, sign in again and retry.' };
  }
  if (kind === 'NotAuthorizedException') return { field: 'current', message: 'The current password is not right.' };
  if (kind === 'InvalidPasswordException' || kind === 'InvalidParameterException') return { field: 'next', message: message ?? 'Cognito refused this password.' };
  if (kind === 'LimitExceededException' || kind === 'TooManyRequestsException') return { message: 'Too many attempts: wait a few minutes and try again.' };
  return { message: `Could not change the password${kind ? ` (${kind})` : ''}. Try again.` };
}
